/** Parse untrusted search parameters once, at the route boundary.
 *
 * The URL owns state that changes what a person is looking at: project scope,
 * filters, pagination, tabs, and selected resources. Components keep only
 * ephemeral interaction state such as drafts, playback, and open menus.
 */

export type UrlStateChange<State extends object> = (
  patch: Partial<State>,
  options?: { readonly replace?: boolean },
) => void;

export function stringParam(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

export function trimmedStringParam(value: unknown): string | undefined {
  return stringParam(value)?.trim();
}

export function enumParam<const Value extends string>(
  value: unknown,
  values: readonly Value[],
): Value | undefined {
  return typeof value === "string" ? values.find((candidate) => candidate === value) : undefined;
}

/** TanStack's default search codec emits arrays as JSON, while accepting repeated
 * parameters and comma-delimited hand-written URLs costs almost nothing here. */
export function enumListParam<const Value extends string>(
  value: unknown,
  values: readonly Value[],
): Value[] | undefined {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  const parsed = raw.flatMap((candidate) => {
    const item = typeof candidate === "string" ? candidate.trim() : "";
    const match = values.find((value) => value === item);
    return match === undefined ? [] : [match];
  });
  const unique = [...new Set(parsed)];
  return unique.length > 0 ? unique : undefined;
}

export function stringListParam(value: unknown, limit?: number): string[] | undefined {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  const parsed = [...new Set(raw.flatMap((candidate) => {
    const item = typeof candidate === "string" ? candidate.trim() : "";
    return item.length > 0 ? [item] : [];
  }))];
  const bounded = limit === undefined ? parsed : parsed.slice(0, limit);
  return bounded.length > 0 ? bounded : undefined;
}

/** How a whole number is written in a URL — digits, and a sign when the caller's bounds
 *  admit one. `Number` alone is not this: it reads `0x10` as 16, `3e2` as 300, and pads
 *  with whitespace, so a path segment or query key that is not an integer at all would
 *  arrive as one. */
const WHOLE_NUMBER = /^-?\d+$/;

export function integerParam(
  value: unknown,
  bounds: { readonly min?: number; readonly max?: number } = {},
): number | undefined {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && WHOLE_NUMBER.test(value)
        ? Number(value)
        : Number.NaN;
  if (!Number.isSafeInteger(parsed)) return undefined;
  if (bounds.min !== undefined && parsed < bounds.min) return undefined;
  if (bounds.max !== undefined && parsed > bounds.max) return undefined;
  return parsed;
}

export function currentCalendarDay(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

export function currentCalendarMonth(date = new Date()): string {
  return currentCalendarDay(date).slice(0, 7);
}

export function calendarDayParam(
  value: unknown,
  bounds: { readonly max?: string } = {},
): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) &&
    date.toISOString().startsWith(value) &&
    (bounds.max === undefined || value <= bounds.max)
    ? value
    : undefined;
}

export function calendarMonthParam(
  value: unknown,
  bounds: { readonly max?: string } = {},
): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}$/.test(value)) return undefined;
  const month = Number(value.slice(5));
  return month >= 1 && month <= 12 && (bounds.max === undefined || value <= bounds.max)
    ? value
    : undefined;
}
